/**
 * Hook optimizado para PlanIndicator - versión que evita refreshes
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import { useSucursal } from '@/context/SucursalContext';
import { usePlanes } from './usePlanes';
import { getPlanIndicatorStats } from '@/services/planStatsService';
import { getTrialConfig } from '@/services/configuracionGlobalService';

// Cache global persistente que sobrevive a navegación
const GLOBAL_CACHE = {
  stats: null,
  trialConfig: null,
  lastEmpresa: null,
  lastSucursal: null,
  lastUpdate: null,
  isInitialized: false
};

const CACHE_DURATION = 1800000; // 30 minutos

// Flag global para evitar múltiples inicializaciones
let isGloballyInitializing = false;

export const usePlanIndicatorStats = () => {
  // Usar SimpleEmpresa directamente
  const { empresaActual } = useSimpleEmpresa();
  const empresaActualId = empresaActual?.id;
  const empresaPlanId = empresaActual?.plan_id;
  
  const { selectedSucursal } = useSucursal();
  const { planes } = usePlanes();
  
  const [stats, setStats] = useState(GLOBAL_CACHE.stats);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trialConfig, setTrialConfig] = useState(GLOBAL_CACHE.trialConfig);
  
  // Usar ref para evitar llamadas múltiples
  const loadingRef = useRef(false);
  
  // Memoizar valores estables para evitar re-renders
  const stableEmpresaId = useMemo(() => empresaActualId, [empresaActualId]);
  const stableSucursalId = useMemo(() => {
    return typeof selectedSucursal === 'number' ? selectedSucursal : selectedSucursal?.id;
  }, [selectedSucursal]);

  // Solo cargar si realmente es necesario (optimización clave)
  const shouldLoadData = useCallback(() => {
    if (!stableEmpresaId || !stableSucursalId) return false;
    
    const now = Date.now();
    const cacheValid = GLOBAL_CACHE.lastEmpresa === stableEmpresaId &&
                      GLOBAL_CACHE.lastSucursal === stableSucursalId &&
                      GLOBAL_CACHE.lastUpdate &&
                      (now - GLOBAL_CACHE.lastUpdate) < CACHE_DURATION;
    
    return !cacheValid;
  }, [stableEmpresaId, stableSucursalId]);

  // Obtener límites del plan actual
  const planLimits = useMemo(() => {
    if (!empresaPlanId || !planes.length) return null;
    
    const currentPlan = planes.find(p => p.id === empresaPlanId);
    if (!currentPlan) return null;
    
    return {
      sucursales: currentPlan.max_sucursales || currentPlan.limites?.sucursales || 1,
      vehiculos: currentPlan.max_vehiculos || currentPlan.limites?.vehiculos || 100,
      mantenimientos: currentPlan.max_mantenimientos_mes || currentPlan.limites?.mantenimientos || 50,
      clientes: currentPlan.max_clientes || currentPlan.limites?.usuarios || 100
    };
  }, [empresaPlanId, planes]);

  // Cargar configuración de trial una sola vez
  useEffect(() => {
    if (GLOBAL_CACHE.trialConfig) {
      setTrialConfig(GLOBAL_CACHE.trialConfig);
      return;
    }

    const loadTrialConfig = async () => {
      try {
        const config = await getTrialConfig();
        GLOBAL_CACHE.trialConfig = config;
        setTrialConfig(config);
      } catch (error) {
        const defaultConfig = {
          trialDays: 30,
          warningDays: 7,
          graceDays: 7,
          enabled: true
        };
        GLOBAL_CACHE.trialConfig = defaultConfig;
        setTrialConfig(defaultConfig);
      }
    };

    loadTrialConfig();
  }, []);

  // Inicialización una sola vez con cache global agresivo
  useEffect(() => {
    // Verificar si ya tenemos todo lo que necesitamos
    if (!stableEmpresaId || !stableSucursalId) {
      console.log('📊 [usePlanIndicatorStats] No hay empresa o sucursal, skipping');
      return;
    }

    // Verificar cache válido primero - este es el paso más importante
    const isCacheValid = GLOBAL_CACHE.lastEmpresa === stableEmpresaId &&
                        GLOBAL_CACHE.lastSucursal === stableSucursalId &&
                        GLOBAL_CACHE.lastUpdate &&
                        (Date.now() - GLOBAL_CACHE.lastUpdate) < CACHE_DURATION;

    if (isCacheValid && GLOBAL_CACHE.stats) {
      console.log('📊 [usePlanIndicatorStats] Using valid cache, no API call needed');
      setStats(GLOBAL_CACHE.stats);
      return;
    }

    // Evitar múltiples llamadas simultáneas globalmente
    if (isGloballyInitializing || loadingRef.current) {
      console.log('📊 [usePlanIndicatorStats] Already initializing globally, skipping');
      return;
    }

    // Solo hacer la llamada si realmente es necesario
    const initializeStats = async () => {
      try {
        console.log('📊 [usePlanIndicatorStats] Initializing stats from API...');
        isGloballyInitializing = true;
        loadingRef.current = true;
        setLoading(true);
        setError(null);
        
        const planStats = await getPlanIndicatorStats(stableEmpresaId, stableSucursalId);
        
        // Actualizar cache global
        GLOBAL_CACHE.stats = planStats;
        GLOBAL_CACHE.lastEmpresa = stableEmpresaId;
        GLOBAL_CACHE.lastSucursal = stableSucursalId;
        GLOBAL_CACHE.lastUpdate = Date.now();
        GLOBAL_CACHE.isInitialized = true;
        
        setStats(planStats);
        console.log('📊 [usePlanIndicatorStats] Stats initialized successfully');
      } catch (err) {
        console.error('❌ [usePlanIndicatorStats] Error initializing stats:', err);
        setError(err.message);
      } finally {
        isGloballyInitializing = false;
        loadingRef.current = false;
        setLoading(false);
      }
    };

    initializeStats();
  }, []); // CLAVE: Array vacío para que solo se ejecute una vez

  // Efecto separado que solo actualiza el estado local si el cache cambia para estos valores específicos
  useEffect(() => {
    if (stableEmpresaId && stableSucursalId) {
      const isCacheForThisContext = GLOBAL_CACHE.lastEmpresa === stableEmpresaId &&
                                    GLOBAL_CACHE.lastSucursal === stableSucursalId &&
                                    GLOBAL_CACHE.stats;

      if (isCacheForThisContext && !stats) {
        console.log('📊 [usePlanIndicatorStats] Syncing local state with cache');
        setStats(GLOBAL_CACHE.stats);
      }
    }
  }, [stableEmpresaId, stableSucursalId, stats]); // Solo actualizar estado local

  // Calcular métricas de uso optimizadas
  const usageMetrics = useMemo(() => {
    if (!stats || !planLimits) return null;

    const calculateUsage = (actual, max) => {
      if (max === -1) {
        return {
          actual,
          max,
          percentage: 0,
          isNearLimit: false,
          isAtLimit: false,
          remaining: Infinity,
          isUnlimited: true
        };
      }
      
      const percentage = Math.min(Math.round((actual / max) * 100), 100);
      return {
        actual,
        max,
        percentage,
        isNearLimit: percentage >= 80,
        isAtLimit: actual >= max,
        remaining: Math.max(max - actual, 0),
        isUnlimited: false
      };
    };

    return {
      sucursales: calculateUsage(stats.sucursales, planLimits.sucursales),
      vehiculos: calculateUsage(stats.vehiculos, planLimits.vehiculos),
      mantenimientos: calculateUsage(stats.mantenimientos, planLimits.mantenimientos),
      clientes: calculateUsage(stats.clientes, planLimits.clientes)
    };
  }, [stats, planLimits]);

  // Calcular porcentaje general de uso
  const overallUsage = useMemo(() => {
    if (!usageMetrics) return 0;

    const weights = {
      mantenimientos: 0.4,
      vehiculos: 0.3,
      sucursales: 0.2,
      clientes: 0.1
    };

    const weightedAverage = Object.entries(weights).reduce((acc, [key, weight]) => {
      return acc + (usageMetrics[key]?.percentage || 0) * weight;
    }, 0);

    return Math.round(weightedAverage);
  }, [usageMetrics]);

  // Determinar métrica más crítica
  const criticalMetric = useMemo(() => {
    if (!usageMetrics) return null;

    const metrics = Object.entries(usageMetrics).map(([key, data]) => ({
      name: key,
      ...data
    }));

    const critical = metrics.reduce((prev, current) => 
      current.percentage > prev.percentage ? current : prev
    );

    return critical.percentage > 0 ? critical : null;
  }, [usageMetrics]);

  // Crear un objeto estable solo con los datos que necesitamos del trial
  const empresaParaTrial = useMemo(() => {
    return empresaActual ? {
      fecha_registro: empresaActual.fecha_registro,
      plan_id: empresaActual.plan_id
    } : null;
  }, [empresaActual?.fecha_registro, empresaActual?.plan_id]);
  
  // Información del trial
  const trialInfo = useMemo(() => {
    if (!empresaParaTrial || !trialConfig) return null;

    const isFreePlan = empresaParaTrial.plan_id === 'revvly_free' || 
                      planes.find(p => p.id === empresaParaTrial.plan_id)?.price === 0;
    
    if (!isFreePlan) return null;

    const fechaRegistro = new Date(empresaParaTrial.fecha_registro);
    const ahora = new Date();
    const diasTranscurridos = Math.floor((ahora - fechaRegistro) / (1000 * 60 * 60 * 24));
    const diasTrial = trialConfig.trialDays;
    const diasAdvertencia = trialConfig.warningDays;
    const diasRestantes = Math.max(diasTrial - diasTranscurridos, 0);
    
    return {
      diasRestantes,
      diasTranscurridos,
      totalDias: diasTrial,
      trialExpirado: diasRestantes === 0,
      trialProximoAExpirar: diasRestantes <= diasAdvertencia && diasRestantes > 0,
      porcentajeUsado: Math.round((diasTranscurridos / diasTrial) * 100)
    };
  }, [empresaParaTrial, planes, trialConfig]);

  // Recomendación de upgrade
  const upgradeRecommendation = useMemo(() => {
    if (!usageMetrics || !planes.length) return null;

    if (trialInfo?.trialExpirado || trialInfo?.trialProximoAExpirar) {
      const currentPlanIndex = planes.findIndex(p => p.id === empresaPlanId);
      const nextPlan = planes[currentPlanIndex + 1];
      
      return nextPlan ? {
        recommended: true,
        targetPlan: nextPlan,
        reason: trialInfo.trialExpirado ? 'Trial expirado' : `Trial expira en ${trialInfo.diasRestantes} días`,
        urgent: trialInfo.trialExpirado
      } : null;
    }

    const hasLimitsIssue = Object.values(usageMetrics).some(metric => 
      metric.isAtLimit || metric.isNearLimit
    );

    if (!hasLimitsIssue) return null;

    const currentPlanIndex = planes.findIndex(p => p.id === empresaPlanId);
    const nextPlan = planes[currentPlanIndex + 1];

    return nextPlan ? {
      recommended: true,
      targetPlan: nextPlan,
      reason: criticalMetric ? `${criticalMetric.name} al ${criticalMetric.percentage}%` : 'Límites próximos'
    } : null;
  }, [usageMetrics, planes, empresaPlanId, criticalMetric, trialInfo]);

  return {
    usageMetrics,
    overallUsage,
    criticalMetric,
    upgradeRecommendation,
    trialInfo,
    loading,
    error
  };
};